import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import {
  auth,
  Color,
  dataStore,
  dataStoreSignal,
  db,
  initDataStoreOrgUser,
  initDataStorePublicSettings,
  initDataStoreRootUser,
} from './app.component';
import { Router } from '@angular/router';
import { User } from 'firebase/auth';
import tinycolor from 'tinycolor2';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ThemeColors } from './interfaces';

export async function saveOrgInfoLocal(orgId: string) {
  if (!dataStore.publicSettings) {
    dataStore.publicSettings = (
      await getDoc(doc(db, `organisations/${orgId}/settings/public`))
    ).data();
  }
  if (!dataStore.selectedOrganisation) {
    dataStore.selectedOrganisation = {
      id: orgId,
      name: dataStore.publicSettings.name,
    };
  }

  dataStore.theming = {
    primaryColor: dataStore.publicSettings.primaryColor ?? '#484b99',
    accentColor: dataStore.publicSettings.accentColor ?? '#42af5a',
    banner: dataStore.publicSettings.banner ?? '../assets/default-images/default-banner.png',
    logo: dataStore.publicSettings.logo ?? '../assets/default-images/default-logo.png',
    name: dataStore.publicSettings.name ?? 'Buurtvervoer',
    favicon: dataStore.publicSettings.favicon ?? '../assets/default-images/default-favicon.ico',
    loginImage:
      dataStore.publicSettings.loginImage ?? '../assets/default-images/default-login-image.svg',
  };

  updateTheme({
    primary: dataStore.theming.primaryColor,
    secondary: dataStore.theming.accentColor,
  });
}

export async function loginDataInit(
  user: User,
  router: Router,
  newAccount: Boolean,
  iconRegistry: MatIconRegistry,
  sanitizer: DomSanitizer,
) {
  await setDoc(
    doc(db, `users/${user.uid}`),
    { selectedOrgId: dataStore.selectedOrganisation.id },
    { merge: true },
  );

  await initDataStoreRootUser(user, iconRegistry, sanitizer);
  await initDataStorePublicSettings();
  await initDataStoreOrgUser();

  if (newAccount || !dataStore.orgUser.name) {
    router.navigate(['/first-time']);
  } else {
    router.navigate(['/profile']);
  }
}

export async function logout(router: Router) {
  dataStore.subscribes.forEach((subscriber) => subscriber.unsub());
  await auth.signOut();
  dataStore.orgUser = null;
  dataStore.rootUser = null;
  dataStoreSignal.set({ ...dataStore });
  return router.navigate(['/auth']);
}

export function sanitizeUrl(link: string) {
  if (link.includes('http://')) {
    return link.replace('http://', 'https://');
  }
  if (!link.includes('https://') && !link.includes('http://')) {
    return `https://${link}`;
  }
  return link;
}

export function formatNumericInput(
  event: Event,
  formGroup: any,
  controlName: string,
  stripDecimals: boolean = false,
): void {
  const input = event.target as HTMLInputElement;
  const originalValue = input.value;
  // Allow digits. Conditionally allow or strip dots and commas.
  const regex = stripDecimals ? /[^0-9]/g : /[^0-9.,]/g;
  const sanitized = originalValue.replace(regex, '');

  if (sanitized !== originalValue) {
    input.value = sanitized;
    if (formGroup && formGroup.get(controlName)) {
      formGroup.get(controlName).setValue(sanitized, { emitEvent: false });
    }
  }
}

export function updateTheme(themeColors: ThemeColors) {
  const palettes = Object.keys(themeColors);
  for (const palette of palettes) {
    if (palette !== 'master') {
      // generate the shades of the palette, based on the base color of the theme palette
      const colors = computeColors(themeColors[palette]);
      // console.log(themeColors[palette]);
      // set colors on all root variables of the palette that the dynamic theme uses
      for (const color of colors) {
        document.documentElement.style.setProperty(`--${palette}-${color.name}`, color.hex);
      }
      // set the root variables for the main color and contrast color of the palette. the contrast variable is used by the dynamic theme to fix some incorrect contrast colors.
      const mainColor = colors.find((color) => color.name === '40');
      document.documentElement.style.setProperty(`--${palette}`, mainColor.hex);
      document.documentElement.style.setProperty(
        `--${palette}-contrast`,
        mainColor.darkContrast ? 'black' : 'white',
      );
    } else if (palette === 'master') {
      const mainColor = getColorObject(tinycolor(themeColors[palette]));
      document.documentElement.style.setProperty(`--${palette}`, mainColor.hex);
      document.documentElement.style.setProperty(
        `--${palette}-contrast`,
        mainColor.darkContrast ? 'black' : 'white',
      );
    }
  }
  updateRippleColors();
}
function computeColors(hex: string): Color[] {
  return [
    getColorObject(tinycolor(hex).darken(30), '10'),
    getColorObject(tinycolor(hex).darken(20), '20'),
    getColorObject(tinycolor(hex).darken(15), '25'),
    getColorObject(tinycolor(hex).darken(10), '30'),
    getColorObject(tinycolor(hex).darken(5), '35'),
    getColorObject(tinycolor(hex), '40'),
    getColorObject(tinycolor(hex).lighten(10), '50'),
    getColorObject(tinycolor(hex).lighten(20), '60'),
    getColorObject(tinycolor(hex).lighten(30), '70'),
    getColorObject(tinycolor(hex).lighten(40), '80'),
    getColorObject(tinycolor(hex).lighten(50), '90'),
    getColorObject(tinycolor(hex).lighten(55), '95'),
    getColorObject(tinycolor(hex).lighten(58), '98'),
    getColorObject(tinycolor(hex).lighten(59), '99'),
  ];
}
export function getColorObject(value, name = ''): Color {
  const c = tinycolor(value);
  return {
    name: name,
    hex: c.toHexString(),
    darkContrast: c.getLuminance() > 0.45,
  };
}
function updateRippleColors() {
  // update Angular ripple variables that do not have rgba when using a dynamic theme, despite needing rgba for the opacity
  const rippleVariables = [
    '--mat-fab-ripple-color',
    '--mat-fab-small-ripple-color',
    '--mat-button-protected-ripple-color',
    '--mat-button-text-ripple-color',
    '--mat-button-outlined-ripple-color',
  ];
  rippleVariables.forEach((rippleVariable) => {
    const rgba = hexToRgba(
      window.getComputedStyle(document.body).getPropertyValue(rippleVariable),
      0.12,
    );
    if (rgba) {
      document.body.style.setProperty(
        rippleVariable,
        `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
      );
    }
  });
}
function hexToRgba(hex, opacity) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: opacity,
      }
    : null;
}

export function valueToDate(value: any): Date | null {
  if (value === null || value === undefined || value === '' || !isNaN(Number(value))) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (value?.toDate) {
    return value.toDate();
  }
  if (value?.seconds !== undefined && value?.nanoseconds !== undefined) {
    return new Timestamp(value.seconds, value.nanoseconds).toDate();
  }

  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}
